import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ClockIcon } from "@heroicons/react/24/outline";
import { getQuizQuestions, submitQuizResults,reportViolation,getHubDetails  } from "../../api/userApi";
import { useToast } from "../../contexts/ToastContext";

const StageOne = () => {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  /* ---------------- CORE STATES ---------------- */
  const [step, setStep] = useState("permissions");
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [hasWarning, setHasWarning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFinished, setIsFinished] = useState(false);
const [resultData, setResultData] = useState(null); // { passed: true/false, score: 0 }

  const arenaRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  /* ---------------- SECURITY & TAB MONITORING ---------------- */
  const sendViolation = async (type, meta = {}) => {
    try { await reportViolation({ competitionId, type, meta }); } catch (err) {}
  };

  const cleanup = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
  };

  useEffect(() => {
    if (step !== "quiz") return;

    const handleViolation = async (reason) => {
  if (hasWarning) {
    // 🚨 THIS IS THE SECOND STRIKE - HARD DISQUALIFY
    setStep("syncing"); // Show a loading state while we disqualify
    
    try {
      await sendViolation("MULTI_VIOLATION", { 
        reason, 
        attempt: 2,
        finalAction: "DISQUALIFIED" 
      });
      
      cleanup(); // Stop camera and exit fullscreen
      
      // Update resultData state to show the "Eliminated" screen
      setResultData({
        passed: false,
        score: "0.0 (DQ)"
      });
      
      setStep("results");
      toast("SYSTEM LOCKED: Disqualified for security breach.", "error");

      // Redirect after showing the failure message
      setTimeout(() => {
        navigate(`/dashboard/competition-hub/${competitionId}`, { replace: true });
      }, 5000);

    } catch (err) {
      console.error("Failed to log disqualification", err);
      navigate("/dashboard");
    }
  } else {
    // ⚠️ FIRST STRIKE - WARNING
    setHasWarning(true);
    await sendViolation("SECURITY_WARNING", { reason, attempt: 1 });
    toast(`FINAL WARNING: ${reason}. Next time you will be disqualified.`, "error");
    
    // Attempt to force back into fullscreen
    if (arenaRef.current && !document.fullscreenElement) {
       arenaRef.current.requestFullscreen().catch(() => {});
    }
  }
};

    const onVisibility = () => document.hidden && handleViolation("Tab Switched/Minimized");
    const onFullscreenExit = () => !document.fullscreenElement && handleViolation("Fullscreen Exited");

    document.addEventListener("visibilitychange", onVisibility);
    document.addEventListener("fullscreenchange", onFullscreenExit);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("fullscreenchange", onFullscreenExit);
    };
  }, [step, hasWarning]);

  /* ---------------- LOAD DATA ---------------- */


// REPLACE the load useEffect in StageOne.jsx
useEffect(() => {
  const load = async () => {
    try {
      const hub = await getHubDetails(competitionId);

      // ✅ Only block on 'locked' — admin hasn't unlocked yet
      if (hub.stage_1_status === 'locked') {
        toast("Stage 1 is not yet open. Wait for admin to start.", "error");
        navigate(`/dashboard/competition-hub/${competitionId}`);
        return;
      }

      // ✅ Block if already completed or failed
      if (hub.stage_1_status === 'completed') {
        toast("You already completed Stage 1!", "success");
        navigate(`/dashboard/competition-hub/${competitionId}`);
        return;
      }

      if (hub.stage_1_status === 'failed') {
        toast("Stage 1 was already attempted and failed.", "error");
        navigate(`/dashboard/competition-hub/${competitionId}`);
        return;
      }

      // ✅ 'unlocked' — load the questions
      const res = await getQuizQuestions(competitionId);
      setQuestions(res);
      setTimeLeft(res[0]?.timer_seconds || 30);
    } catch {
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };
  load();
}, [competitionId]);


  /* ---------------- TIMER ---------------- */
useEffect(() => {
  if (step !== "quiz" || isFinished) return;

  if (timeLeft === 0) {
    forceSubmitCurrent();
    return;
  }

  const timer = setInterval(() => {
    setTimeLeft(prev => prev - 1);
  }, 1000);

  return () => clearInterval(timer);
}, [timeLeft, step, isFinished]);



  /* ---------------- START ---------------- */
  const startBattle = async () => {
    try {
      const videoStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720 },
        audio: false,
      });
      streamRef.current = videoStream;
      setStartTime(Date.now());
      setStep("quiz");

      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = videoStream;
      }, 150);

      await arenaRef.current.requestFullscreen({ navigationUI: "hide" });
    } catch (err) {
      toast("Camera required for the Arena!", "error");
    }
  };


const forceSubmitCurrent = () => {
  if (isFinished) return;

  const q = questions[currentIndex];

  const updatedAnswers = {
    ...answers,
    //  Store -1 for skipped/unanswered — never null
    // null causes Number(null)===0 which matches index 0 (option A)
    [q.id]: selectedOption !== null ? selectedOption : -1,
  };

  setAnswers(updatedAnswers);
  setSelectedOption(null);

  if (currentIndex < questions.length - 1) {
    const nextIndex = currentIndex + 1;
    setCurrentIndex(nextIndex);
    setTimeLeft(questions[nextIndex]?.timer_seconds || 15);
  } else {
    setIsFinished(true);
    finishTest(updatedAnswers);
  }
};
  
const finishTest = async (finalAnswers) => {
 
  const totalTime = Math.floor((Date.now() - startTime) / 1000);
  
  // 1. Enter a 'syncing' state immediately to stop the quiz UI
  setStep("syncing");

  try {
    const response = await submitQuizResults(competitionId, {
      answers: finalAnswers,
      timeTaken: totalTime,
    });

    // 2. Kill camera and exit fullscreen
    cleanup();

    // 3. Store the result and show the Results Page
    setResultData({
      passed: response.passed,
      score: response.score.toFixed(1)
    });

    setStep("results");

    // 4. Wait 5 seconds, then navigate to Hub
    setTimeout(() => {
      navigate(`/dashboard/competition-hub/${competitionId}`, {
        state: {
          quizResult: response.passed ? "PASSED" : "FAILED",
          finalScore: response.score.toFixed(1),
        },
        replace: true,
      });
    }, 6000); // 6 seconds gives them time to read the message
    

  } catch (err) {
    cleanup();
    toast("ERROR: Data Sync Failed.", "error");
    setStep("permissions"); // Fallback
  }
};


/* ---------------- CLEANUP ON UNMOUNT ---------------- */
useEffect(() => {
  return () => {
    cleanup(); // stops camera + exits fullscreen if component unmounts
  };
}, []);


  if (loading) return <div className="h-screen bg-black text-white flex items-center justify-center font-mono animate-pulse">SYNCING ARENA...</div>;

  return (
    <div ref={arenaRef} className="h-screen w-full bg-black text-white select-none overflow-hidden font-sans flex flex-col relative">
      
      <AnimatePresence mode="wait">
        {step === "permissions" ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-col items-center justify-center">
            <h1 className="text-5xl font-black mb-8 italic text-blue-500">ARE YOU READY ?</h1>
            <button onClick={startBattle} className="px-16 py-5 bg-blue-600 rounded-xl font-bold text-xl hover:scale-105 transition-all">
              START
            </button>
          </motion.div>
        ) : (
          <div className="flex-1 flex flex-col p-6 md:p-10 min-h-0">
            
            {/* TOP BAR */}
            <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
              <div className="bg-white/5 px-4 py-1.5 rounded-lg text-white font-mono border border-white/10">
                QUESTION <span className="text-blue-400 font-bold">{currentIndex + 1}</span> / {questions.length}
              </div>
              <div className={`flex items-center gap-3 font-mono text-2xl text-white`}>
                <ClockIcon className={`h-6 w-6 ${timeLeft < 10 ? 'text-red-500 animate-pulse' : 'text-blue-400'}`} />
                <span className={timeLeft < 10 ? 'text-red-500' : ''}>{timeLeft}s</span>
              </div>
            </div>

            {/* MAIN CONTENT - scrollable container to keep submit button visible */}
            <div className="flex-1 flex flex-col gap-6 min-h-0">
              
              {/* THE QUESTION BOX - Shrunk text slightly to save space */}
              <div className="w-full bg-white/5 border border-white/10 p-6 rounded-2xl">
                <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-white leading-snug">
                  {questions[currentIndex]?.question_text}
                </h2>
              </div>

              {/* LOWER SECTION */}
              <div className="grid grid-cols-12 gap-6 flex-1 min-h-0">
                
                {/* OPTIONS AREA - Scrollable if options are too many */}
                <div className="col-span-12 lg:col-span-8 overflow-y-auto pr-2 custom-scrollbar">
                  <div className="flex flex-col gap-3">
                    {questions[currentIndex]?.options.map((opt, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedOption(i)}
                        className={`group flex items-center text-left px-6 py-4 border-2 rounded-xl transition-all
                          ${selectedOption === i 
                            ? "bg-blue-600 border-blue-400 shadow-lg" 
                            : "border-white/5 bg-white/5 hover:border-white/20"}
                        `}
                      >
                        <span className={`w-8 h-8 rounded-md flex items-center justify-center mr-4 font-bold border 
                          ${selectedOption === i ? 'bg-white text-blue-600 border-white' : 'border-white/20 text-white'}`}>
                          {String.fromCharCode(65 + i)}
                        </span>
                        <span className="text-lg font-medium flex-1 text-white">{opt}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* CIRCLE CAMERA - Simplified */}
                <div className="hidden lg:flex lg:col-span-4 justify-center items-start">
                  <div className="relative w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden border-4 border-blue-500/30 bg-gray-900 shadow-2xl">
                    <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover scale-x-[-1]" />
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-red-600 px-2 py-0.5 rounded-full text-[8px] font-black flex items-center gap-1">
                      <span className="w-1 h-1 bg-white rounded-full animate-pulse" /> LIVE
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ACTION FOOTER - Stays fixed at bottom */}
            <div className="mt-6 pt-4 border-t border-white/10 flex justify-center">
              <button
                 onClick={forceSubmitCurrent}
                disabled={selectedOption === null}
                className="px-16 py-4 bg-blue-600 rounded-xl font-bold text-xl uppercase tracking-widest disabled:opacity-30 hover:bg-blue-700 transition-all shadow-lg"
              >
                {currentIndex === questions.length - 1 ? "FINAL SUBMIT" : "SUBMIT ANSWER"}
              </button>
            </div>

          </div>
        )}
      </AnimatePresence>

      {/* --- ADD THIS CELEBRATION OVERLAY LAYER --- */}
      <AnimatePresence>
        {(step === "syncing" || step === "results") && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md"
          >
            {/* Case 1: The Syncing Screen */}
            {step === "syncing" && (
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4 mx-auto"></div>
                <p className="font-mono tracking-[0.3em] animate-pulse">UPLOADING BATTLE DATA...</p>
              </div>
            )}

            {/* Case 2: The Final Results Celebration */}
            {step === "results" && resultData && (
              <motion.div 
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                className="max-w-md w-full p-10 bg-[#0a0a0a] border border-white/10 rounded-[2.5rem] text-center shadow-2xl"
              >
                {resultData.passed ? (
                  <>
                    <div className="text-7xl mb-4 animate-bounce">🎉</div>
                    <h2 className="text-4xl font-black text-green-500 italic uppercase mb-2">Victory!</h2>
                    <p className="text-gray-400 mb-6 text-lg">You cleared Stage 1 with <span className="text-white font-bold">{resultData.score}%</span></p>
                    <div className="py-2 px-6 bg-green-500/10 border border-green-500/20 rounded-full inline-block text-green-500 text-xs font-bold tracking-widest uppercase">
                      Stage 2 Unlocked
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-7xl mb-4">❌</div> 
                    <h2 className="text-4xl font-black text-red-500 italic uppercase mb-2">Eliminated</h2>
                    <p className="text-gray-400 mb-6 text-lg">Your score: <span className="text-white font-bold">{resultData.score}%</span></p>
                    <div className="py-2 px-6 bg-red-500/10 border border-red-500/20 rounded-full inline-block text-red-400 text-xs font-bold tracking-widest uppercase">
                      Min. 70% required to advance
                    </div>
                  </>
                )}

                {/* Progress Bar (Matches your 6-second timeout) */}
                <div className="mt-10 space-y-2">
                  <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }} 
                      animate={{ width: "100%" }} 
                      transition={{ duration: 6, ease: "linear" }} 
                      className="h-full bg-blue-600 shadow-[0_0_10px_#2563eb]"
                    />
                  </div>
                  <p className="text-[9px] uppercase tracking-[0.3em] text-gray-600 font-bold">Returning to Competition Hub</p>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StageOne;





